import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { openModal } from '../../../store/slices/uiSlice';
import useFlightSearch from '../../../hooks/useFlightSearch';
import airportService from '../../../services/airportService';
import flightSearchService from '../../../services/flightSearchService';
import bookingService from '../../../services/bookingService';
import { navigateWithBookingData } from '../../../utils/bookingQueryParams';
import {
  getPartialChangeContext,
  legToSelectableFlight,
  findMatchingSearchFlight,
  resolveOutboundRouteId,
} from '../../../utils/partialLegChange';

export const useFlightSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const searchParams = new URLSearchParams(location.search);

  // Get authentication state
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // Flight search hook
  const {
    loading: searchLoading,
    error: searchError,
    searchFlights: performSearch,
    getFlightDetails,
    sortFlights
  } = useFlightSearch();

  // Local state
  const [outboundFlights, setOutboundFlights] = useState([]);
  const [returnFlights, setReturnFlights] = useState([]);
  const [multiCitySegments, setMultiCitySegments] = useState([]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedFlights, setSelectedFlights] = useState([]);
  const [activeFlightTab, setActiveFlightTab] = useState('outbound');
  const [filters, setFilters] = useState({
    lowestPax: false,
    fastestFlightTime: false,
    morning: false,
    afternoon: false,
    evening: false,
    sortBy: 'default'
  });

  const didUserChangeTimeOfDayRef = useRef(false);
  const [departureAirport, setDepartureAirport] = useState(null);
  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [enrichedSegments, setEnrichedSegments] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [originalBookingFlights, setOriginalBookingFlights] = useState([]);

  const partialChangeInitRef = useRef(false);

  const getSelectedTimeOfDay = (currentFilters = filters) => {
    const slots = [];
    if (currentFilters.morning) slots.push('morning');
    if (currentFilters.afternoon) slots.push('afternoon');
    if (currentFilters.evening) slots.push('evening');
    return slots;
  };

  // Determine flight type and mode based on URL path
  const getFlightConfig = () => {
    const path = location.pathname;
    let flightType = 'one-way';
    let mode = 'search';

    if (path.includes('one-way')) flightType = 'one-way';
    else if (path.includes('return')) flightType = 'return';
    else if (path.includes('multi-city')) flightType = 'multi-city';
    else if (path.includes('flight-list')) flightType = 'one-way';

    if (path.includes('search') || path.includes('list') || path.includes('flight')) {
      mode = location.state?.selectedFlights ? 'details' : 'search';
    }

    return { flightType, mode };
  };

  const { flightType, mode } = getFlightConfig();
  const totalPassengers = parseInt(searchParams.get('passengers') || '2');

  // Get search parameters with defaults
  const parseChangeLegsParam = (searchParams) => {
    const raw = searchParams.get('change_legs');
    if (!raw) return [];
    return raw.split(',').map((token) => token.trim()).filter(Boolean);
  };

  const getSearchData = () => {
    const tripType = searchParams.get('tripType') || flightType;

    if (flightType === 'multi-city') {
      const segmentCount = parseInt(searchParams.get('segmentCount') || '2');
      const segments = [];

      for (let i = 1; i <= segmentCount; i++) {
        const from = searchParams.get(`from${i}`);
        const to = searchParams.get(`to${i}`);
        const date = searchParams.get(`date${i}`);

        if (from && to && date) {
          segments.push({ from, to, date, segmentNumber: i });
        }
      }

      return {
        segments,
        passengers: totalPassengers,
        tripType,
        booking_token: searchParams.get('booking_token'),
        change_legs: parseChangeLegsParam(searchParams),
        page: parseInt(searchParams.get('page') || '1')
      };
    }

    return {
      from: searchParams.get('from') || (flightType === 'return' ? 'AAR' : 'AAL'),
      to: searchParams.get('to') || (flightType === 'return' ? 'TAB' : 'TAB'),
      departure: searchParams.get('departure') || 'Fri 31 Jan 26',
      return: searchParams.get('return') || 'Sat 31 Jan 26',
      passengers: totalPassengers,
      tripType,
      booking_token: searchParams.get('booking_token'),
      change_legs: parseChangeLegsParam(searchParams),
      page: parseInt(searchParams.get('page') || '1')
    };
  };

  const searchData = getSearchData();

  const partialChange = useMemo(
    () => getPartialChangeContext(originalBookingFlights, searchData.change_legs),
    [originalBookingFlights, searchData.change_legs]
  );

  // Load original booking legs for partial rebooking
  useEffect(() => {
    const token = searchParams.get('booking_token');
    const changeLegs = parseChangeLegsParam(searchParams);
    if (!token || !changeLegs.length) {
      setOriginalBookingFlights([]);
      partialChangeInitRef.current = false;
      return;
    }

    let cancelled = false;
    const fetchOriginalBooking = async () => {
      try {
        const response = await bookingService.getBookingByReference(token);
        if (!cancelled && response?.success && response?.data?.flights?.length) {
          setOriginalBookingFlights(response.data.flights);
          partialChangeInitRef.current = false;
        }
      } catch (err) {
        console.error('Failed to load original booking for partial change:', err);
      }
    };

    fetchOriginalBooking();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Fetch airport names/codes based on IDs
  useEffect(() => {
    const fetchAirportDetails = async () => {
      const idsToFetch = new Set();

      if (flightType === 'multi-city') {
        searchData.segments.forEach(seg => {
          if (seg.from && !isNaN(parseInt(seg.from))) idsToFetch.add(parseInt(seg.from));
          if (seg.to && !isNaN(parseInt(seg.to))) idsToFetch.add(parseInt(seg.to));
        });
      } else {
        if (searchData.from && !isNaN(parseInt(searchData.from))) idsToFetch.add(parseInt(searchData.from));
        if (searchData.to && !isNaN(parseInt(searchData.to))) idsToFetch.add(parseInt(searchData.to));
      }

      if (idsToFetch.size === 0) return;

      try {
        const response = await airportService.getAirportsByIds(Array.from(idsToFetch));
        const airportMap = {};
        if (response && response.data) {
          response.data.forEach(airport => {
            airportMap[airport.id] = airport;
          });
        }

        if (flightType === 'multi-city') {
          const enriched = searchData.segments.map(seg => ({
            ...seg,
            fromAirport: airportMap[seg.from] || { airport_name: seg.from, iata: '' },
            toAirport: airportMap[seg.to] || { airport_name: seg.to, iata: '' }
          }));
          setEnrichedSegments(enriched);
        } else {
          setDepartureAirport(airportMap[searchData.from]);
          setArrivalAirport(airportMap[searchData.to]);
        }
      } catch (err) {
        console.error('Failed to fetch airport details:', err);
      }
    };

    fetchAirportDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, flightType]);

  // Fetch flights based on URL parameters or state
  useEffect(() => {
    console.log('useFlightSelection: Fetching flights', {
      hasState: !!location.state,
      mode,
      searchParams: location.search
    });

    // Check if we have selected flights from previous step (details mode)
    if (location.state?.selectedFlights) {
      console.log('Using selected flights from state');
      setSelectedFlights(location.state.selectedFlights);
      setLoading(false);
      return;
    }

    // Use cached search results if available
    if (location.state?.searchResults && location.state?.fromSearch) {
      const results = location.state.searchResults;
      setOutboundFlights(results.outboundFlights || []);
      setReturnFlights(results.returnFlights || []);
      setMultiCitySegments(results.segments || []);
      if (results.pagination) {
        setPaginationInfo(results.pagination);
      }
      setLoading(false);
      return;
    }

    if (mode === 'details' && !location.state?.selectedFlights) {
      const fetchFlight = async () => {
        const id = location.pathname.split('/').pop();
        const details = await getFlightDetails(id);
        setSelectedFlights([details]);
        setLoading(false);
      };
      fetchFlight();
      return;
    }

    // Perform search based on URL parameters
    const performSearchFromParams = async () => {
      const searchData = {
        from: searchParams.get('from'),
        to: searchParams.get('to'),
        departure: searchParams.get('departure'),
        return: searchParams.get('return'),
        passengers: parseInt(searchParams.get('passengers') || '1'),
        tripType: searchParams.get('tripType') || 'One Way',
        nearby: searchParams.get('nearby') === 'true',
        booking_token: searchParams.get('booking_token'),
        page: parseInt(searchParams.get('page') || '1'),
        time_of_day: (searchParams.get('time_of_day') || '')
          .split(',')
          .map((slot) => slot.trim().toLowerCase())
          .filter(Boolean)
      };

      // Handle multi-city search
      if (searchData.tripType && (searchData.tripType.toLowerCase() === 'multicity' || searchData.tripType.toLowerCase() === 'multi-city')) {
        const segmentCount = parseInt(searchParams.get('segmentCount') || '2');
        const segments = [];

        for (let i = 1; i <= segmentCount; i++) {
          const from = searchParams.get(`from${i}`);
          const to = searchParams.get(`to${i}`);
          const date = searchParams.get(`date${i}`);

          if (from && to && date) {
            segments.push({ from, to, date, segmentNumber: i });
          }
        }

        searchData.segments = segments;
      }

      // Only search if we have required parameters
      const isMultiCityTrip = searchData.tripType && searchData.tripType.toLowerCase().includes('multi');

      if (isMultiCityTrip) {
        if (searchData.segments && searchData.segments.length >= 2) {
          try {
            setLoading(true);
            const results = await performSearch(searchData);
            if (results && results.segments) {
              setMultiCitySegments(results.segments);
            } else {
              setMultiCitySegments([]);
            }
            setActiveSegmentIndex(
              partialChange.isPartialChange && partialChange.editableSegmentIndices.length
                ? partialChange.editableSegmentIndices[0]
                : 0
            );
          } catch (error) {
            setMultiCitySegments([]);
          } finally {
            setLoading(false);
          }
        }
      } else if (searchData.from && searchData.to && searchData.departure) {
        try {
          setLoading(true);
          const results = await performSearch(searchData);
          setOutboundFlights(results.outboundFlights || []);
          setReturnFlights(results.returnFlights || []);
          if (results.pagination) {
            setPaginationInfo(results.pagination);
          }
        } catch (error) {
          setOutboundFlights([]);
          setReturnFlights([]);
          setPaginationInfo(null);
        } finally {
          setLoading(false);
        }
      }
    };

    // Skip search if we're on return tab (return flights already loaded dynamically)
    if (flightType === 'return' && activeFlightTab === 'return') {
      console.log('⏭️ Skipping performSearch - return flights already loaded dynamically');
      return;
    }

    performSearchFromParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state, flightType, activeFlightTab]);

  // Initialize partial leg-change flow (preserve unchanged legs, open editable leg)
  useEffect(() => {
    if (partialChangeInitRef.current) return;
    if (loading || searchLoading) return;
    if (!partialChange.isPartialChange || !originalBookingFlights.length) return;
    if (location.state?.selectedFlights) return;

    if (
      flightType === 'return' &&
      partialChange.changingReturn &&
      !partialChange.changingOutbound
    ) {
      // Wait until outbound search results are loaded so we have a valid route id
      if (outboundFlights.length === 0) return;

      partialChangeInitRef.current = true;
      const outboundLeg = partialChange.sortedOriginalFlights[0];
      const preservedOutbound =
        findMatchingSearchFlight(outboundFlights, outboundLeg) ||
        legToSelectableFlight(outboundLeg);

      setSelectedFlights([preservedOutbound]);

      const fetchReturnForPreservedOutbound = async () => {
        try {
          const outboundRouteId = resolveOutboundRouteId(
            preservedOutbound,
            outboundLeg,
            outboundFlights
          );
          const returnDate = flightSearchService.formatDateForAPI(searchData.return);

          if (!outboundRouteId || !returnDate) {
            throw new Error(
              !returnDate
                ? 'Return date is missing or invalid.'
                : 'Could not resolve outbound flight for return search.'
            );
          }

          setLoading(true);
          const returnFlightsResult = await flightSearchService.searchReturnFlights({
            departure_airport: preservedOutbound.arrival_airport,
            arrival_airport: preservedOutbound.departure_airport,
            return_date: returnDate,
            outbound_flight_id: outboundRouteId,
            passengers: searchData.passengers,
            time_of_day: getSelectedTimeOfDay(),
            page: 1,
          });
          setReturnFlights(returnFlightsResult.returnFlights || []);
          if (returnFlightsResult.pagination) {
            setPaginationInfo(returnFlightsResult.pagination);
          }
          setActiveFlightTab('return');
          toast.success('Outbound leg unchanged — select a new return flight.');
        } catch (error) {
          partialChangeInitRef.current = false;
          console.error('Failed to load return flights for partial change:', error);
          toast.error(error?.message || 'Failed to load return flights. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      fetchReturnForPreservedOutbound();
      return;
    }

    if (
      flightType === 'return' &&
      partialChange.changingOutbound &&
      !partialChange.changingReturn
    ) {
      partialChangeInitRef.current = true;
      toast.success('Return leg unchanged — select a new outbound flight only.');
      return;
    }

    if (flightType === 'multi-city' && multiCitySegments.length > 0) {
      partialChangeInitRef.current = true;
      const prefilled = [];
      partialChange.sortedOriginalFlights.forEach((leg, idx) => {
        if (partialChange.lockedSegmentIndices.includes(idx)) {
          prefilled[idx] = legToSelectableFlight(leg);
        }
      });
      setSelectedFlights(prefilled);
      const firstEditable = partialChange.editableSegmentIndices[0];
      if (firstEditable !== undefined) {
        setActiveSegmentIndex(firstEditable);
      }
      toast.success(
        `Only leg ${partialChange.editableSegmentIndices.map((i) => i + 1).join(', ')} can be changed.`
      );
    }
  }, [
    loading,
    searchLoading,
    partialChange,
    originalBookingFlights,
    outboundFlights,
    multiCitySegments,
    flightType,
    searchData.return,
    searchData.passengers,
    location.state,
  ]);

  // When URL changes (navigation or our own updates), treat next checkbox sync as non-user action
  // until the user toggles time-of-day again.
  useEffect(() => {
    didUserChangeTimeOfDayRef.current = false;
  }, [location.search, mode]);

  // Keep URL search params in sync with time-of-day checkbox filters
  useEffect(() => {
    if (mode !== 'search') return;

    const params = new URLSearchParams(location.search);
    const slots = (params.get('time_of_day') || '')
      .split(',')
      .map((slot) => slot.trim().toLowerCase())
      .filter(Boolean);

    const next = {
      morning: slots.includes('morning'),
      afternoon: slots.includes('afternoon'),
      evening: slots.includes('evening')
    };

    setFilters(prev => {
      const isSame =
        prev.morning === next.morning &&
        prev.afternoon === next.afternoon &&
        prev.evening === next.evening;

      if (isSame) return prev;
      return { ...prev, ...next };
    });
  }, [location.search, mode]);

  // Keep URL search params in sync with time-of-day checkbox filters
  useEffect(() => {
    if (mode !== 'search') return;

    const selectedSlots = getSelectedTimeOfDay();
    const params = new URLSearchParams(location.search);
    const urlHasTimeOfDay = (params.get('time_of_day') || '').trim().length > 0;

    // Prevent accidental deletion/overwrite of `time_of_day` while we are hydrating
    // checkbox state from the current URL.
    if (urlHasTimeOfDay && !didUserChangeTimeOfDayRef.current) return;

    const currentSlots = (params.get('time_of_day') || '')
      .split(',')
      .map((slot) => slot.trim().toLowerCase())
      .filter(Boolean);

    const hasSameSlots = selectedSlots.length === currentSlots.length &&
      selectedSlots.every((slot, index) => slot === currentSlots[index]);

    if (hasSameSlots) return;

    if (selectedSlots.length > 0) {
      params.set('time_of_day', selectedSlots.join(','));
    } else {
      params.delete('time_of_day');
    }

    // New filter selection should restart pagination from first page
    params.set('page', '1');
    navigate({ search: params.toString() }, { replace: true });
  }, [filters.morning, filters.afternoon, filters.evening, location.search, mode, navigate]);

  // Get filtered and sorted flights
  const getDisplayFlights = () => {
    let displayFlights = [];

    if (flightType === 'multi-city' && mode === 'search') {
      const activeSegment = multiCitySegments[activeSegmentIndex];
      displayFlights = activeSegment?.flights || [];
    } else if (flightType === 'return' && mode === 'search') {
      displayFlights = activeFlightTab === 'outbound' ? outboundFlights : returnFlights;
    } else {
      displayFlights = outboundFlights;
    }

    // Time-of-day filtering is server-side only for this page.
    // Checkbox changes update `time_of_day` in URL, which triggers a fresh API search.

    // Determine effective sort — lowestPax/fastestFlightTime checkboxes map to sort options
    let effectiveSort = filters.sortBy;
    if (filters.lowestPax) effectiveSort = 'price_asc';
    if (filters.fastestFlightTime) effectiveSort = 'duration_asc';

    displayFlights = sortFlights(displayFlights, effectiveSort);

    return displayFlights;
  };

  // Handle flight selection — use search list data; analytics runs on getFlightDetails in booking flow
  const handleFlightSelect = (flight) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      dispatch(openModal('login'));
      return;
    }

    try {
      const selectedFlight = flight;
      const searchData = getSearchData();
      const isMultiCity = searchData.tripType && (searchData.tripType.toLowerCase() === 'multicity' || searchData.tripType.toLowerCase() === 'multi-city');
      const isReturnFlight = searchData.tripType === 'Returns' || searchData.tripType === 'return';

      if (isReturnFlight && partialChange.isPartialChange) {
        if (activeFlightTab === 'outbound' && !partialChange.changingOutbound) {
          toast.error('The outbound leg is unchanged and cannot be modified.');
          return;
        }
        if (activeFlightTab === 'return' && !partialChange.changingReturn) {
          toast.error('The return leg is unchanged and cannot be modified.');
          return;
        }
      }

      if (isMultiCity && partialChange.isPartialChange && partialChange.lockedSegmentIndices.includes(activeSegmentIndex)) {
        toast.error('This leg is unchanged and cannot be modified.');
        return;
      }

      const buildFullMulticitySelection = (updatedSelectedFlights) =>
        partialChange.sortedOriginalFlights.map((leg, idx) => {
          if (partialChange.lockedSegmentIndices.includes(idx)) {
            return updatedSelectedFlights[idx] || legToSelectableFlight(leg);
          }
          return updatedSelectedFlights[idx];
        });

      const getNextEditableSegmentIndex = (fromIndex) => {
        for (let i = fromIndex + 1; i < multiCitySegments.length; i++) {
          if (!partialChange.lockedSegmentIndices.includes(i)) return i;
        }
        return -1;
      };

      const navigateToMulticityBooking = (fullSelection) => {
        const bookingData = {
          flight_id: fullSelection[0].id,
          aircraft_id: fullSelection[0].aircraft_id,
          registration_number: fullSelection[0].registration_number,
          flight_number: fullSelection[0].flight_number,
          departure_airport: fullSelection[0].departure_airport,
          departure_date_time: fullSelection[0].departure?.datetime,
          arrival_airport: fullSelection[0].arrival_airport,
          arrival_date_time: fullSelection[0].arrival?.datetime,
          passengers: searchData.passengers || 1,
          routeType: 'Multicity',
          selectedFlights: fullSelection,
        };

        if (searchData.booking_token) {
          bookingData.booking_token = searchData.booking_token;
        }
        if (searchData.change_legs?.length) {
          bookingData.change_legs = searchData.change_legs;
        }

        navigateWithBookingData(
          navigate,
          `/flight-booking-flow/${fullSelection[0].id}`,
          bookingData,
          {
            state: {
              selectedFlight: fullSelection[0],
              selectedFlights: fullSelection,
              searchParams: searchData,
              fromUrl: location.pathname + location.search,
            },
          }
        );
      };

      if (isMultiCity) {
        const updatedSelectedFlights = [...selectedFlights];
        updatedSelectedFlights[activeSegmentIndex] = selectedFlight;
        setSelectedFlights(updatedSelectedFlights);
        toast.success(`Flight ${activeSegmentIndex + 1} selected successfully`);

        const nextEditable = getNextEditableSegmentIndex(activeSegmentIndex);
        if (nextEditable >= 0) {
          setActiveSegmentIndex(nextEditable);
          handlePageChange(1);
          return;
        }

        const fullSelection = partialChange.isPartialChange
          ? buildFullMulticitySelection(updatedSelectedFlights)
          : updatedSelectedFlights.slice(0, activeSegmentIndex + 1);

        navigateToMulticityBooking(fullSelection);
        return;
      }

      if (isReturnFlight) {
        if (activeFlightTab === 'outbound') {
          if (
            partialChange.isPartialChange &&
            partialChange.changingOutbound &&
            !partialChange.changingReturn
          ) {
            const preservedReturnLeg = partialChange.sortedOriginalFlights[1];
            const preservedReturn = legToSelectableFlight(preservedReturnLeg);
            const outboundId = selectedFlight.id;

            const bookingData = {
              flight_id: outboundId,
              return_flight_id: preservedReturn.id || preservedReturn.routes_id,
              aircraft_id: selectedFlight.aircraft_id,
              registration_number: selectedFlight.registration_number,
              flight_number: selectedFlight.flight_number,
              departure_airport: selectedFlight.departure_airport,
              departure_date_time: selectedFlight.departure?.datetime,
              arrival_airport: selectedFlight.arrival_airport,
              arrival_date_time: selectedFlight.arrival?.datetime,
              passengers: searchData.passengers || 1,
              routeType: searchData.tripType || 'Returns',
              from: searchData.from,
              to: searchData.to,
              departure: searchData.departure,
              selectedFlights: [selectedFlight, preservedReturn],
            };

            if (searchData.booking_token) {
              bookingData.booking_token = searchData.booking_token;
            }
            if (searchData.change_legs?.length) {
              bookingData.change_legs = searchData.change_legs;
            }
            if (searchData.return) {
              bookingData.return = searchData.return;
            }

            navigateWithBookingData(
              navigate,
              `/flight-booking-flow/${outboundId}`,
              bookingData,
              {
                state: {
                  selectedFlight,
                  selectedFlights: [selectedFlight, preservedReturn],
                  searchParams: searchData,
                  fromUrl: location.pathname + location.search,
                },
              }
            );
            return;
          }

          setSelectedFlights([selectedFlight]);
          
          // Fetch return flights dynamically after outbound selection
          const fetchReturnFlights = async () => {
            try {
              setLoading(true);
              
              // Same return date as initial /flights/search (searchData.return from URL/form).
              const returnDate = flightSearchService.formatDateForAPI(searchData.return);

              console.log('🔄 Fetching return flights after outbound selection:', {
                outboundFlight: selectedFlight.flight_number,
                outboundDeparture: selectedFlight.departure?.datetime || selectedFlight.departure_date_time,
                outboundArrival: selectedFlight.arrival?.datetime || selectedFlight.arrival_date_time,
                returnDate,
                departureAirport: selectedFlight.arrival_airport,
                arrivalAirport: selectedFlight.departure_airport,
                passengers: searchData.passengers,
                earliestDepartureTime: selectedFlight.arrival_date_time
              });
              
              // Call the NEW return flights endpoint
              const returnFlightsResult = await flightSearchService.searchReturnFlights({
                departure_airport: selectedFlight.arrival_airport,
                arrival_airport: selectedFlight.departure_airport,
                return_date: returnDate,
                outbound_flight_id: selectedFlight.id,  // Pass the flight ID
                passengers: searchData.passengers,
                time_of_day: getSelectedTimeOfDay(),
                page: 1
              });
              
              console.log('✅ Return flights API response:', returnFlightsResult);
              
              // Update state with return flights
              const returnFlightsList = returnFlightsResult.returnFlights || [];
              
              console.log('📊 Return Flights Received:', {
                count: returnFlightsList.length,
                flights: returnFlightsList.map(f => ({
                  flight_number: f.flightNumber,
                  departure_time: f.departure?.datetime,
                  arrival_time: f.arrival?.datetime
                }))
              });
              setReturnFlights(returnFlightsList);
              
              if (returnFlightsResult.pagination) {
                setPaginationInfo(returnFlightsResult.pagination);
              }
              
              console.log('✅ Return flights fetched and state updated:', {
                count: returnFlightsList.length,
                flights: returnFlightsList.map(f => f.flight_number)
              });
              
              // Switch to return tab AFTER flights are loaded
              setActiveFlightTab('return');
              handlePageChange(1);
              toast.success(`Outbound flight selected. ${returnFlightsList.length} return flights available.`);
            } catch (error) {
              console.error('❌ Failed to fetch return flights:', error);
              toast.error('Failed to load return flights. Please try again.');
              setReturnFlights([]);
              setActiveFlightTab('outbound');
            } finally {
              setLoading(false);
            }
          };
          
          // Execute the async function
          fetchReturnFlights();
          return;
        } else {
          const existingOutbound = selectedFlights[0];
          if (!existingOutbound) {
            toast.error('Please select outbound flight first.');
            setActiveFlightTab('outbound');
            return;
          }

          const outboundId = existingOutbound.id;
          const returnId = selectedFlight.id;

          const bookingData = {
            flight_id: outboundId,
            return_flight_id: returnId,
            aircraft_id: existingOutbound.aircraft_id,
            registration_number: existingOutbound.registration_number,
            flight_number: existingOutbound.flight_number,
            departure_airport: existingOutbound.departure_airport,
            departure_date_time: existingOutbound.departure?.datetime,
            arrival_airport: existingOutbound.arrival_airport,
            arrival_date_time: existingOutbound.arrival?.datetime,
            passengers: searchData.passengers || 1,
            routeType: searchData.tripType || 'Returns',
            from: searchData.from,
            to: searchData.to,
            departure: searchData.departure,
            selectedFlights: [existingOutbound, selectedFlight]
          };

          // Only add booking_token if this is a flight change (rebooking)
          if (searchData.booking_token) {
            bookingData.booking_token = searchData.booking_token;
          }
          if (searchData.change_legs?.length) {
            bookingData.change_legs = searchData.change_legs;
          }

          if (searchData.return) {
            bookingData.return = searchData.return;
          }

          navigateWithBookingData(
            navigate,
            `/flight-booking-flow/${outboundId}`,
            bookingData,
            {
              state: {
                selectedFlight: existingOutbound,
                selectedFlights: [existingOutbound, selectedFlight],
                searchParams: searchData,
                fromUrl: location.pathname + location.search
              }
            }
          );
          return;
        }
      }

      // One-way flight - navigate directly
      const bookingData = {
        flight_id: selectedFlight.id,
        aircraft_id: selectedFlight.aircraft_id,
        registration_number: selectedFlight.registration_number,
        flight_number: selectedFlight.flight_number,
        departure_airport: selectedFlight.departure_airport,
        departure_date_time: selectedFlight.departure?.datetime,
        arrival_airport: selectedFlight.arrival_airport,
        arrival_date_time: selectedFlight.arrival?.datetime,
        passengers: searchData.passengers || 1,
        routeType: searchData.tripType || 'One Way',
        from: searchData.from,
        to: searchData.to,
        departure: searchData.departure
      };

      // Only add booking_token if this is a flight change (rebooking)
      if (searchData.booking_token) {
        bookingData.booking_token = searchData.booking_token;
      }
      if (searchData.change_legs?.length) {
        bookingData.change_legs = searchData.change_legs;
      }

      navigateWithBookingData(
        navigate,
        `/flight-booking-flow/${selectedFlight.id}`,
        bookingData,
        {
          state: {
            selectedFlight,
            searchParams: searchData,
            fromUrl: location.pathname + location.search
          }
        }
      );
    } catch (error) {
      console.error('Flight selection error:', error);
      toast.error('Failed to select flight. Please try again.');
    }
  };

  const handleFilterChange = (filterType, value) => {
    if (['morning', 'afternoon', 'evening'].includes(filterType)) {
      didUserChangeTimeOfDayRef.current = true;
    }
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleResetFilters = () => {
    didUserChangeTimeOfDayRef.current = true;
    setFilters({
      lowestPax: false,
      fastestFlightTime: false,
      morning: false,
      afternoon: false,
      evening: false,
      sortBy: 'default'
    });
    handlePageChange(1);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(location.search);
    params.set('page', newPage);
    navigate({ search: params.toString() });
  };

  const handleNext = () => {
    const hasUnavailableSeats = selectedFlights.some(flight => flight.remainingSeat === 0);

    if (hasUnavailableSeats) {
      toast.error('There is no seat available on this select flight, please select other flight.');
      return;
    }

    // Navigate to flight booking flow for all trip types
    const flightId = selectedFlights[0]?.id;
    navigate(`/flight-booking-flow/${flightId}`, {
      state: {
        selectedFlights,
        totalPassengers,
        searchData,
        routeType: flightType === 'multi-city' ? 'Multicity' : flightType === 'return' ? 'Returns' : 'One Way'
      }
    });
  };

  const isActiveLegLocked = () => {
    if (flightType === 'return' && partialChange.isPartialChange) {
      if (activeFlightTab === 'outbound') return !partialChange.changingOutbound;
      if (activeFlightTab === 'return') return !partialChange.changingReturn;
    }
    if (flightType === 'multi-city' && partialChange.isPartialChange) {
      return partialChange.lockedSegmentIndices.includes(activeSegmentIndex);
    }
    return false;
  };

  const getPreservedFlightForActiveLeg = () => {
    if (flightType === 'return' && partialChange.sortedOriginalFlights.length >= 2) {
      const legIndex = activeFlightTab === 'outbound' ? 0 : 1;
      const leg = partialChange.sortedOriginalFlights[legIndex];
      return selectedFlights[legIndex] || legToSelectableFlight(leg);
    }
    if (flightType === 'multi-city') {
      const leg = partialChange.sortedOriginalFlights[activeSegmentIndex];
      return selectedFlights[activeSegmentIndex] || (leg ? legToSelectableFlight(leg) : null);
    }
    return null;
  };

  const handleContinueFromLockedLeg = async () => {
    if (!partialChange.isPartialChange || !isActiveLegLocked()) return;

    if (flightType === 'return') {
      if (activeFlightTab === 'outbound' && partialChange.changingReturn) {
        const outboundLeg = partialChange.sortedOriginalFlights[0];
        const preservedOutbound =
          selectedFlights[0] ||
          findMatchingSearchFlight(outboundFlights, outboundLeg) ||
          legToSelectableFlight(outboundLeg);

        if (!selectedFlights[0]) {
          setSelectedFlights([preservedOutbound]);
        }

        try {
          if (returnFlights.length === 0) {
            const outboundRouteId = resolveOutboundRouteId(
              preservedOutbound,
              outboundLeg,
              outboundFlights
            );
            const returnDate = flightSearchService.formatDateForAPI(searchData.return);

            if (!outboundRouteId || !returnDate) {
              throw new Error(
                !returnDate
                  ? 'Return date is missing or invalid.'
                  : 'Could not resolve outbound flight for return search.'
              );
            }

            setLoading(true);
            const returnFlightsResult = await flightSearchService.searchReturnFlights({
              departure_airport: preservedOutbound.arrival_airport,
              arrival_airport: preservedOutbound.departure_airport,
              return_date: returnDate,
              outbound_flight_id: outboundRouteId,
              passengers: searchData.passengers,
              time_of_day: getSelectedTimeOfDay(),
              page: 1,
            });
            setReturnFlights(returnFlightsResult.returnFlights || []);
            if (returnFlightsResult.pagination) {
              setPaginationInfo(returnFlightsResult.pagination);
            }
          }
          setActiveFlightTab('return');
          handlePageChange(1);
        } catch (error) {
          console.error('Failed to load return flights:', error);
          toast.error(error?.message || 'Failed to load return flights. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }

      if (activeFlightTab === 'return' && partialChange.changingOutbound) {
        setActiveFlightTab('outbound');
        handlePageChange(1);
      }
      return;
    }

    if (flightType === 'multi-city') {
      const nextEditable = partialChange.editableSegmentIndices.find(
        (index) => index > activeSegmentIndex
      );
      if (nextEditable !== undefined) {
        setActiveSegmentIndex(nextEditable);
        handlePageChange(1);
      }
    }
  };

  return {
    // State
    outboundFlights,
    returnFlights,
    multiCitySegments,
    activeSegmentIndex,
    loading: loading || searchLoading,
    selectedFlights,
    activeFlightTab,
    filters,
    flightType,
    mode,
    totalPassengers,
    searchData,
    searchError,
    departureAirport,
    arrivalAirport,
    enrichedSegments,
    paginationInfo,
    partialChange,
    isActiveLegLocked,
    getPreservedFlightForActiveLeg,
    handleContinueFromLockedLeg,

    // Functions
    getDisplayFlights,
    handleFlightSelect,
    handleFilterChange,
    handleResetFilters,
    handlePageChange,
    setActiveFlightTab,
    setActiveSegmentIndex,
    handleNext
  };
};
